import { Cartesian3, Color, ScreenSpaceEventHandler, defined, ScreenSpaceEventType, Math as CesiumMath, CustomDataSource } from "cesium";

export const addPolylineBetweenGeoJsonPoints = (viewer, features, options = {}) => {
  // Validate the input parameters
  if (!viewer || !features || !Array.isArray(features) || features.length === 0) {
    console.warn("Input parameters are invalid");
    return null;
  }

  // Map over the features to extract their coordinates and convert them to Cartesian3
  const positions = features
    .map((feature, index) => {
      const coords = feature.geometry?.coordinates;

      // Check if coordinates exist and are valid numbers
      if (!Array.isArray(coords) || coords.length < 2 || typeof coords[0] !== "number" || typeof coords[1] !== "number" || isNaN(coords[0]) || isNaN(coords[1])) {
        console.warn(`Invalid coordinates for feature at index ${index}:`, coords);
        return null;
      }

      // Convert to Cartesian3
      return Cartesian3.fromDegrees(coords[0], coords[1]);
    })
    .filter((position) => position !== null);

  // Ensure there are at least two valid points to form a line
  if (positions.length < 2) {
    console.warn("At least 2 points are required to create a line.");
    return null;
  }

  // Default options for the polyline if none are provided
  const defaultOptions = {
    width: 5, // Default line width
    color: Color.RED.withAlpha(0.6), // Default color with transparency
    id: "polyline-" + Date.now(), // Generate a unique ID for the polyline
    name: "Polyline between points", // Default name for the polyline
  };

  // Merge the default options with the user-provided options
  const polylineOptions = { ...defaultOptions, ...options };

  // Add the polyline entity to the viewer
  const entity = viewer.entities.add({
    id: polylineOptions.id,
    name: polylineOptions.name,
    polyline: {
      positions: positions, // Positions for the line
      width: polylineOptions.width, // Line width
      material: polylineOptions.color instanceof Color ? polylineOptions.color : Color.RED.withAlpha(0.6), // Line color
      clampToGround: options.clampToGround || false, // Option to clamp the line to the ground
    },
  });

  // Return the created polyline entity
  return entity;
};

export const createArrowsBetweenPositions = (viewer, features, options = {}) => {
  // Map over the features to extract coordinates and convert them to Cartesian3
  const positions = features
    .map((feature, index) => {
      const coords = feature.geometry?.coordinates;

      if (!Array.isArray(coords) || coords.length < 2 || typeof coords[0] !== "number" || typeof coords[1] !== "number" || isNaN(coords[0]) || isNaN(coords[1])) {
        console.warn(`Skipping feature at index ${index} due to invalid coordinates:`, coords);
        return null;
      }

      return Cartesian3.fromDegrees(coords[0], coords[1]);
    })
    .filter((pos) => pos !== null);

  // Default configuration options for the arrows
  const defaultOptions = {
    arrowImage: "/assets/image/arrow.png", // Default image for the arrow
    scale: 0.3, // Default scale for the arrow size
    angleAdjustment: 45, // Angle adjustment in degrees
  };

  // Merge default options with the provided options
  const config = { ...defaultOptions, ...options };

  // Array to store the created arrow entities
  const arrowEntities = [];

  // Loop through each pair of consecutive positions to create arrows
  for (let index = 0; index < positions.length - 1; index++) {
    const position = positions[index];
    const nextPosition = positions[index + 1];

    // Calculate the midpoint between the current position and the next position
    const midPoint = Cartesian3.midpoint(position, nextPosition, new Cartesian3());

    // Calculate the direction vector between the two points
    const direction = Cartesian3.subtract(nextPosition, position, new Cartesian3());

    // Calculate the angle for arrow rotation (in radians) and add the angle adjustment
    let angle = Math.atan2(direction.z, direction.x) + CesiumMath.toRadians(config.angleAdjustment);

    // Create an arrow entity at the midpoint, with the specified rotation and scale
    const arrowEntity = viewer.entities.add({
      position: midPoint,
      billboard: {
        image: config.arrowImage, // Image for the arrow
        scale: config.scale, // Scale of the image
        rotation: -angle, // Negative because Cesium handles rotation inversely
      },
    });

    // Add the created arrow entity to the array
    arrowEntities.push(arrowEntity);
  }

  // Return the array of created arrow entities
  return arrowEntities;
};

export const loadGeojsonPointsOptimized = (viewer, geojsonData, fileName, onFeatureClick) => {
  const dataSource = new CustomDataSource(fileName);

  geojsonData.features.forEach((feature, index) => {
    const coordinates = feature.geometry?.coordinates;

    if (!Array.isArray(coordinates) || coordinates.length < 2 || typeof coordinates[0] !== "number" || typeof coordinates[1] !== "number" || isNaN(coordinates[0]) || isNaN(coordinates[1])) {
      console.warn(`Invalid coordinates for feature at index ${index}:`, coordinates);
      return; // skip this feature
    }

    dataSource.entities.add({
      id: "gje-" + index,
      name: `point ${index + 1}`,
      position: Cartesian3.fromDegrees(coordinates[0], coordinates[1]),
      description: JSON.stringify(feature.properties, null, 2),
      point: {
        pixelSize: 8, // initial size
        color: Color.RED.withAlpha(1),
        outlineColor: Color.BLACK,
        outlineWidth: 1,
      },
    });
  });

  viewer.dataSources.add(dataSource);

  // ✅ Global post-render to adjust point size dynamically
  const adjustPointSize = () => {
    const cameraHeight = viewer.camera.positionCartographic.height;
    let scaleFactor = 10 - cameraHeight / 100000;
    scaleFactor = Math.max(1, Math.min(10, scaleFactor));

    dataSource.entities.values.forEach((entity) => {
      if (entity.point) {
        entity.point.pixelSize = scaleFactor;
      }
    });
  };
  viewer.scene.postRender.addEventListener(adjustPointSize);

  // ✅ Global click handler for all entities
  const eventHandler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  eventHandler.setInputAction((click) => {
    const picked = viewer.scene.pick(click.position);
    if (defined(picked) && picked.id?.id?.startsWith("gje-")) {
      const id = picked.id.id;
      const entity = picked.id;
      const index = parseInt(id.split("-")[1]);
      const feature = geojsonData.features[index];
      viewer.selectedEntity = entity;
      if (onFeatureClick && typeof onFeatureClick === "function") {
        onFeatureClick(feature, index);
      }
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  // ✅ Return handles in case you want to clean up later
  return dataSource;
};

export const getCameraImageTypeName = (name) => {
  switch (name?.toLowerCase()) {
    case "ff_rh_120":
      return "Front Facing - Right Hand - Focal 120";
    case "ff_lh_120":
      return "Front Facing - Left Hand - Focal 120";
    case "f_30":
      return "Front - Focal 30";
    case "f_120":
      return "Front - Focal 120";
    case "rf_lh_120":
      return "Rear Facing - Left Hand - Focal 120";
    case "f_60":
      return "Front - Focal 60";
    case "rf_rh_120":
      return "Rear Facing - Reft Hand - Focal 120";
  }

  return "";
};
